import React, { useEffect, useState } from 'react';
import { Braces, Check, Copy, X } from 'lucide-react';
import { apiMessage } from '../services/serviceInboxApi';

/**
 * JsonModal — the raw data behind something on the page, for the curious.
 *
 * Used by the Mailbox for a ServiceTitan customer read live from ServiceTitan,
 * and for everything the service inbox stored about an email (the AI side:
 * prompts, model calls, the lookup). `load` fetches when the modal opens.
 *
 * `sections` split the data into tabs: `{ label, pick(data), text }`. `pick`
 * returns the part to show; `text` shows a string as written (a prompt reads
 * better with its line breaks than as one escaped JSON string). An "All" tab
 * always comes last with the whole payload. Copy copies the tab on screen.
 */

const TabBody = ({ value, text }) => {
  if (value === undefined || value === null) {
    return <div className="p-3 text-sm text-gray-500">Nothing here.</div>;
  }
  if (text && typeof value === 'string') {
    return (
      <pre className="whitespace-pre-wrap break-words p-4 text-sm leading-relaxed text-gray-900">
        {value}
      </pre>
    );
  }
  return (
    <pre className="whitespace-pre-wrap break-words p-4 font-mono text-sm leading-relaxed text-gray-900">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

const JsonModal = ({ title, subtitle, load, sections = [], onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    load()
      .then((d) => live && setData(d))
      .catch((err) => live && setError(apiMessage(err, 'Could not load the data.')));
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs = [...sections, { label: 'All', pick: (d) => d }];
  const current = tabs[tab];
  const value = data ? current.pick(data) : null;

  const copy = async () => {
    const s = current.text && typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    try {
      await navigator.clipboard.writeText(s);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no clipboard access (insecure origin): the text is still on screen */
    }
  };

  return (
    <div
      data-json-modal
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-7xl flex-col overflow-hidden bg-white shadow-xl sm:h-[90vh] sm:rounded-lg"
      >
        <div className="flex items-start gap-2 border-b border-gray-200 px-4 py-3">
          <Braces className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-gray-900">{title}</div>
            {subtitle && <div className="truncate text-xs text-gray-500">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={copy}
            disabled={!data}
            className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {copied ? (
              <Check className="mr-1 h-3.5 w-3.5 text-green-700" />
            ) : (
              <Copy className="mr-1 h-3.5 w-3.5" />
            )}
            Copy
          </button>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {tabs.length > 1 && (
          <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 px-2">
            {tabs.map((t, i) => (
              <button
                key={t.label}
                type="button"
                onClick={() => setTab(i)}
                className={`shrink-0 border-b-2 px-3 py-2 text-sm ${
                  i === tab
                    ? 'border-blue-600 font-medium text-blue-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto bg-gray-50">
          {error && <div className="p-3 text-sm text-red-700">{error}</div>}
          {!data && !error && <div className="p-3 text-sm text-gray-500">Loading…</div>}
          {data && <TabBody value={value} text={current.text} />}
        </div>
      </div>
    </div>
  );
};

// A small `{ }` button that opens a JsonModal.
export const JsonButton = ({ onClick, label, className = '' }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title={label}
    aria-label={label}
    className={`inline-flex shrink-0 items-center rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 ${className}`}
  >
    <Braces className="h-3.5 w-3.5" />
  </button>
);

export default JsonModal;
